"use client";

import { useMemo, useState, useSyncExternalStore } from "react";
import { AnimatePresence, MotionConfig, motion } from "motion/react";
import { Link } from "@/i18n/navigation";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";
import useSolopreneurProfile from "@/hooks/useSolopreneurProfile";
import { dossierVide } from "@/lib/tools/dossier";
import { outilsDuDossier } from "@/lib/tools/dossierSituation";
import { dateDepuisIso, reponsesDesOutils } from "@/lib/tools/situation";

/**
 * L'index des outils : trouver le bon parmi vingt-neuf.
 *
 * VINGT-NEUF CARTES ÉGALES NE RECOMMANDENT RIEN, et c'était la version
 * précédente. Celui qui arrive avec une question précise la tape dans la
 * recherche ; celui qui arrive sans en avoir une repart sans rien ouvrir. Trois
 * vedettes ouvrent donc la rubrique, et s'effacent dès qu'une recherche ou un
 * filtre dit ce que le lecteur cherche.
 *
 * LE PREMIER FILTRE POSE LA QUESTION DU MOMENT, plus celle du thème. « Sur quel
 * sujet : obligations et conformité, formalités et documents » demandait au
 * lecteur de connaître le classement de l'administration avant de chercher.
 * Personne n'arrive avec ça en tête : on arrive avec « je me lance », « ce
 * client ne me paie pas », « il me reste combien ». Les quatre moments sont
 * dans le registre (MOMENTS), dans l'ordre d'une première année, et les
 * sections du résultat suivent le même ordre.
 *
 * LA FORME PASSE EN SECOND, en liste déroulante : « est-ce que je vais calculer
 * un montant, vérifier si je suis concerné, ou repartir avec un papier ? » reste
 * une bonne question, mais elle vient après celle de la situation, et deux
 * contrôles de forme différente ne se confondent pas.
 *
 * UNE LIGNE PAR OUTIL, PAS UNE CARTE (27/09/2026). Quarante-deux cartes à
 * description complète faisaient un annuaire qu'on survole sans lire. Chaque
 * outil tient désormais sur une ligne : son icône, son nom, une phrase, et sa
 * réponse quand le dossier en donne une. La description entière reste dans le
 * HTML (coupée à l'écran seulement) pour les moteurs de recherche.
 *
 * QUAND LE DOSSIER EXISTE, LES TROIS VEDETTES DEVIENNENT LES SIENNES : les
 * outils que sa situation rend utiles (outilsDuDossier), et non ceux par
 * lesquels la plupart commencent.
 *
 * TOUT EST RENDU CÔTÉ SERVEUR À L'ARRIVÉE : l'état de départ n'a ni filtre ni
 * recherche, si bien que les vingt-neuf liens sont dans le HTML initial et
 * restent lisibles par un moteur de recherche. Le composant ne reçoit d'ailleurs
 * que ce que la liste affiche, pas les fiches pédagogiques du registre, qui
 * pèsent cent fois plus.
 */

/** Le verbe et le libellé de chaque forme d'outil. */
// Le parcours vient en premier : c'est le seul qui réponde à « par où
// commencer », et c'est la question de qui arrive sans savoir quoi chercher.
const FORMES = ["parcours", "simulateur", "diagnostic", "formulaire"];

/** L'icône qui distingue une forme, en un coup d'œil. */
const ICONE_FORME = {
    parcours: "map-arrow-right-linear",
    simulateur: "calculator-minimalistic-linear",
    diagnostic: "shield-check-linear",
    formulaire: "document-text-linear",
};

/** L'icône de chaque moment : une situation se reconnaît plus vite qu'elle ne se lit. */
const ICONE_MOMENT = {
    lancement: "rocket-linear",
    clients: "bill-list-linear",
    argent: "wallet-money-linear",
    protection: "shield-user-linear",
};

/** Retire accents et ponctuation : « décennale » se trouve en tapant « decennale ». */
function aplatir(texte) {
    return texte
        .toLowerCase()
        .normalize("NFD")
        .replace(/[̀-ͯ]/g, "")
        .replace(/[^a-z0-9]+/g, " ")
        .trim();
}

/**
 * La couleur d'un statut. Le texte reste toujours sombre sur fond clair, et
 * clair sur fond sombre : seul le point et le filet portent la couleur, parce
 * qu'un texte orange ou rouge sur un fond de la même teinte ne se lit plus.
 */
const COULEURS_STATUT = {
    urgent: {
        point: "bg-red-500",
        fond: "bg-red-50 dark:bg-red-500/10",
        bord: "border-red-300 dark:border-red-500/40",
    },
    attention: {
        point: "bg-amber-500",
        fond: "bg-amber-50 dark:bg-amber-500/10",
        bord: "border-amber-300 dark:border-amber-500/40",
    },
    ok: {
        point: "bg-emerald-500",
        fond: "bg-emerald-50 dark:bg-emerald-500/10",
        bord: "border-emerald-300/70 dark:border-emerald-500/30",
    },
    info: {
        point: "bg-primary",
        fond: "bg-gray-50 dark:bg-white/[0.04]",
        bord: "border-gray-200/70 dark:border-white/10",
    },
};

function euros(valeur, locale) {
    return new Intl.NumberFormat(locale === "en" ? "en-GB" : "fr-FR", {
        style: "currency",
        currency: "EUR",
        maximumFractionDigits: 0,
    }).format(Math.round(valeur));
}

function dateLongue(date, locale) {
    return new Intl.DateTimeFormat(locale === "en" ? "en-GB" : "fr-FR", {
        day: "numeric",
        month: "long",
        year: "numeric",
    }).format(new Date(date));
}

/** La réponse d'un outil, dite avec les mots de la page. */
function phraseDeLaReponse(r, t, locale) {
    let type = r.type;
    // Le pluriel des trimestres ne se règle pas par un « (s) ».
    if (type === "retraitePartielle" && r.valides === 0) type = "retraiteAucun";
    if (type === "retraitePartielle" && r.valides === 1) type = "retraiteUn";
    const modele = t.reponses[type];
    if (!modele) return null;
    const phrase = modele
        .replace("{ca}", euros(r.caMensuel ?? r.caProchain ?? 0, locale))
        .replace("{objectif}", euros(r.objectifMensuel ?? 0, locale))
        .replace("{jours}", r.jours ?? "")
        .replace("{tarif}", euros(r.tarifJournalier ?? 0, locale))
        .replace(
            "{part}",
            new Intl.NumberFormat(locale === "en" ? "en-GB" : "fr-FR", {
                style: "percent",
                maximumFractionDigits: 0,
            }).format(r.part ?? 0),
        )
        .replace("{plafond}", euros(r.plafond ?? 0, locale))
        .replace("{avant}", euros(r.avantBase ?? 0, locale))
        .replace("{base}", euros(r.base ?? 0, locale))
        .replace("{majore}", euros(r.majore ?? 0, locale))
        .replace("{valides}", r.valides ?? "")
        .replace("{gain}", euros(r.gain ?? 0, locale))
        .replace("{remuneration}", euros(r.remuneration ?? 0, locale))
        .replace("{ecart}", euros(r.ecart ?? 0, locale))
        .replace("{seuil}", euros(r.seuil ?? 0, locale))
        .replace(
            "{parJour}",
            new Intl.NumberFormat(locale === "en" ? "en-GB" : "fr-FR", {
                style: "currency",
                currency: "EUR",
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
            }).format(r.parJour ?? 0),
        )
        .replace("{date}", r.date ? dateLongue(r.date, locale) : "")
        .replace("{annee}", r.date ? new Date(r.date).getFullYear() : "");
    // Un métier réglementé peut être un métier de soin : le cas des soins se
    // dit d'abord, et le verdict des seuils ne vaut que « sinon ».
    if (r.soins && t.reponsesSoinsTva) {
        return `${t.reponsesSoinsTva} ${t.reponsesSinon}${phrase.charAt(0).toLowerCase()}${phrase.slice(1)}`;
    }
    return phrase;
}

/** Le compte à rebours d'une échéance, quand il reste moins de trois mois. */
function delaiDeLaReponse(r, t) {
    // Une date passée n'a pas de compte à rebours : la phrase la dit déjà.
    if (r.passe) return null;
    if (r.joursRestants == null || r.joursRestants > 90) return null;
    if (r.joursRestants <= 0) return t.reponsesAujourdhui;
    if (r.joursRestants === 1) return t.reponsesDemain;
    return t.reponsesDans.replace("{jours}", r.joursRestants);
}

/**
 * La réponse d'un outil, posée sur sa ligne : un point de couleur et le
 * verdict, avec le compte à rebours s'il presse. La page d'accueil de la
 * rubrique ne détaille plus : l'outil le fait, un clic plus loin.
 */
function ReponseDeLOutil({ reponse, t, locale }) {
    const phrase = phraseDeLaReponse(reponse, t, locale);
    if (!phrase) return null;
    const couleurs = COULEURS_STATUT[reponse.statut];
    const delai = delaiDeLaReponse(reponse, t);
    return (
        <span className="mt-1.5 flex items-start gap-2 text-[13px]">
            <span className={`mt-[5px] h-2 w-2 shrink-0 rounded-full ${couleurs.point}`} aria-hidden="true" />
            <span className="font-semibold text-gray-800 dark:text-white">
                <span className="sr-only">
                    {t.statuts[reponse.statut]}
                    {locale === "en" ? ": " : " : "}
                </span>
                {delai ? phrase.replace(/\.$/, "") : phrase}
                {delai && <span className="font-normal text-gray-500 dark:text-lightBlue/70"> · {delai}</span>}
            </span>
        </span>
    );
}

/** L'adresse ne change pas d'elle-même pendant qu'on est sur la page. */
function abonnerAdresse() {
    return () => {};
}

/** Réécrit la recherche et les filtres dans l'adresse, sans étape d'historique. */
function ecrireAdresse(champs) {
    try {
        const p = new URLSearchParams(window.location.search);
        for (const [cle, valeur] of Object.entries(champs)) {
            if (valeur) p.set(cle, valeur);
            else p.delete(cle);
        }
        const texte = p.toString();
        window.history.replaceState(window.history.state, "", `${window.location.pathname}${texte ? `?${texte}` : ""}${window.location.hash}`);
    } catch (e) {
        // Sans historique disponible, la liste marche quand même.
    }
}

function Pastille({ actif, onClick, children, compte }) {
    return (
        <button
            type="button"
            onClick={onClick}
            aria-pressed={actif}
            className={`shrink-0 inline-flex min-h-[44px] items-center gap-2 rounded-full border px-3.5 py-1.5 text-sm transition-colors duration-200 ${
                actif
                    ? "border-primary/50 bg-primary/10 text-[#B83C12] dark:text-[#FF8A5B] font-semibold"
                    : "border-gray-200/70 dark:border-white/10 bg-white/60 dark:bg-white/[0.03] text-gray-600 dark:text-lightBlue hover:border-primary/40"
            }`}
        >
            {children}
            {compte !== undefined && (
                <span
                    className={`text-xs tabular-nums ${
                        actif ? "text-[#B83C12] dark:text-[#FF8A5B]" : "text-gray-500 dark:text-lightBlue/70"
                    }`}
                >
                    {compte}
                </span>
            )}
        </button>
    );
}

export default function ToolsExplorer({
    outils,
    t,
    locale,
    moments,
    libellesMoments,
    vedettes,
}) {
    // LA RECHERCHE ET LES FILTRES VIVENT DANS L'ADRESSE. Sans cela, ouvrir un
    // outil puis revenir en arrière vidait tout, et une liste filtrée ne se
    // partageait pas. L'adresse se lit comme une source externe (le serveur
    // la voit vide, le navigateur la lit après l'hydratation), et chaque
    // changement la réécrit sans ajouter d'étape à l'historique.
    const query = useSyncExternalStore(
        abonnerAdresse,
        () => window.location.search,
        () => "",
    );
    const depuisAdresse = useMemo(() => {
        const p = new URLSearchParams(query);
        return {
            q: p.get("q") || "",
            moment: moments.includes(p.get("moment")) ? p.get("moment") : null,
            forme: FORMES.includes(p.get("forme")) ? p.get("forme") : null,
        };
    }, [query, moments]);
    const [momentLibre, setMomentLibre] = useState(undefined);
    const [formeLibre, setFormeLibre] = useState(undefined);
    const [rechercheLibre, setRechercheLibre] = useState(undefined);
    const moment = momentLibre !== undefined ? momentLibre : depuisAdresse.moment;
    const forme = formeLibre !== undefined ? formeLibre : depuisAdresse.forme;
    const recherche = rechercheLibre !== undefined ? rechercheLibre : depuisAdresse.q;
    const setMoment = (v) => {
        setMomentLibre(v);
        ecrireAdresse({ moment: v });
    };
    const setForme = (v) => {
        setFormeLibre(v);
        ecrireAdresse({ forme: v });
    };
    const setRecherche = (v) => {
        setRechercheLibre(v);
        ecrireAdresse({ q: v.trim() });
    };

    // LES LIGNES RÉPONDENT AVANT QU'ON LES OUVRE. Les outils qui savent tirer
    // quelque chose du dossier l'affichent, calculé par leurs propres moteurs.
    // Le serveur rend le profil vide, donc la liste arrive dans l'ordre du
    // registre et les réponses se posent après, sans que l'hydratation diverge.
    const { profil } = useSolopreneurProfile();
    const reponses = useMemo(() => {
        const dateDebut = dateDepuisIso(profil.dateDebut);
        return reponsesDesOutils({
            activite: profil.objectifMensuel ? profil.activite || "bnc" : profil.activite,
            objectifMensuel: profil.objectifMensuel,
            tarifJournalier: profil.tarifJournalier,
            dateDebut,
            // Hors micro, seuls les outils qui s'appliquent répondent.
            statut: profil.statut || "micro",
        });
    }, [profil]);

    // Les compteurs se calculent sur le catalogue entier, jamais sur le
    // résultat filtré : une pastille qui change de nombre quand on clique une
    // autre pastille est illisible.
    const comptes = useMemo(() => {
        const parMoment = {};
        const parForme = {};
        for (const o of outils) {
            parMoment[o.moment] = (parMoment[o.moment] || 0) + 1;
            parForme[o.type] = (parForme[o.type] || 0) + 1;
        }
        return { parMoment, parForme };
    }, [outils]);

    // Les vedettes gardent l'ordre du registre : c'est une recommandation, et
    // elle se lit de gauche à droite. Avec un dossier, ce sont les siennes.
    const avecDossier = !dossierVide(profil);
    const misEnAvant = useMemo(() => {
        const cles = avecDossier ? outilsDuDossier(profil).slice(0, 3) : vedettes;
        return cles.map((cle) => outils.find((o) => o.cle === cle)).filter(Boolean);
    }, [outils, vedettes, avecDossier, profil]);

    const termes = useMemo(
        () => aplatir(recherche).split(" ").filter(Boolean),
        [recherche],
    );

    const retenus = useMemo(
        () =>
            outils.filter((o) => {
                if (moment && o.moment !== moment) return false;
                if (forme && o.type !== forme) return false;
                if (!termes.length) return true;
                // La recherche tape dans le nom, la description, le slug et
                // les renvois de glossaire : « 1447 » doit trouver la CFE, et
                // « décennale » les assurances obligatoires, alors que le mot
                // n'est dans aucun des trois premiers.
                const foin = aplatir(
                    `${o.nom} ${o.description} ${o.slug} ${(o.mots || []).join(" ")}`,
                );
                return termes.every((m) => foin.includes(m));
            }),
        [outils, moment, forme, termes],
    );

    // Le résultat garde ses sections par moment, sauf quand on a déjà filtré
    // sur un moment : un seul titre au-dessus d'une seule section ne dit rien.
    const sections = useMemo(() => {
        if (moment) return [[moment, retenus]];
        const par = {};
        for (const o of retenus) (par[o.moment] ||= []).push(o);
        return moments.filter((m) => par[m]).map((m) => [m, par[m]]);
    }, [retenus, moment, moments]);

    const filtre = Boolean(moment || forme || termes.length);
    const lien = (o) => ({ pathname: "/tools/[slug]", params: { slug: o.slug } });

    return (
        <MotionConfig reducedMotion="user">
        <div>

            {/* TROIS OUTILS AVANT LES QUARANTE-DEUX. Ils s'effacent dès que le
                lecteur a dit ce qu'il cherchait : une recommandation n'a plus
                de sens une fois la demande formulée. */}
            <AnimatePresence initial={false}>
                {!filtre && misEnAvant.length > 0 && (
                    <motion.div
                        key="vedettes"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0, height: 0, marginBottom: 0 }}
                        transition={{ duration: 0.2, ease: "easeOut" }}
                        className="mb-8 overflow-hidden"
                    >
                        <div className="text-[11px] font-semibold text-gray-600 dark:text-grayDark-400 uppercase tracking-wider mb-2.5">
                            {avecDossier ? t.pourVous : t.vedettes}
                        </div>
                        <div className="grid sm:grid-cols-3 gap-3">
                            {misEnAvant.map((o) => (
                                <Link
                                    key={o.cle}
                                    href={lien(o)}
                                    className="group flex items-center gap-3 rounded-2xl border border-primary/25 bg-primary/[0.04] dark:bg-primary/[0.07] px-4 py-3.5 hover:border-primary/60 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_10px_28px_-14px_rgba(232,81,29,0.45)] dark:hover:shadow-[0_10px_28px_-14px_rgba(232,81,29,0.3)]"
                                >
                                    <span className="shrink-0 w-9 h-9 rounded-xl bg-primary/10 flex items-center justify-center">
                                        <SolarIcon name={o.icone || ICONE_FORME[o.type]} className="w-5 h-5 text-primary" />
                                    </span>
                                    <span className="min-w-0">
                                        <span className="block font-bold text-sm dark:text-white leading-snug transition-colors group-hover:text-primary">
                                            {o.nom}
                                        </span>
                                        {/* group-hover l'emporte sur la règle
                                            globale a:hover, qui repeindrait
                                            sinon ce libellé en rose. */}
                                        <span className="block text-xs text-[#B83C12] dark:text-[#FF8A5B] mt-0.5">
                                            {t.verbes[o.type]} →
                                        </span>
                                    </span>
                                </Link>
                            ))}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* UNE SEULE BARRE DE FILTRES : la recherche et la forme côte à
                côte, les moments dessous. Les intitulés d'axe en capitales
                sont partis ; les lecteurs d'écran les gardent. */}
            <div className="flex flex-col gap-2.5 sm:flex-row">
                <div className="relative flex-1">
                    <SolarIcon
                        name="magnifer-linear"
                        className="absolute left-4 top-1/2 -translate-y-1/2 text-lg text-gray-400 dark:text-lightBlue/60 pointer-events-none"
                    />
                    <input
                        type="search"
                        value={recherche}
                        onChange={(e) => setRecherche(e.target.value)}
                        placeholder={t.rechercher}
                        aria-label={t.rechercher}
                        enterKeyHint="search"
                        className="w-full rounded-2xl border border-gray-200/70 dark:border-white/10 bg-white/60 dark:bg-white/[0.03] pl-11 pr-4 py-3 text-base dark:text-white placeholder:text-gray-500 dark:placeholder:text-lightBlue/70 focus:outline-hidden focus:border-primary/60 focus-visible:ring-2 focus-visible:ring-[#D04515]/50 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-bgDark-800 transition-colors duration-200"
                    />
                </div>
                <div className="relative sm:w-56">
                    <label htmlFor="outils-forme" className="sr-only">
                        {t.axeForme}
                    </label>
                    <select
                        id="outils-forme"
                        value={forme || ""}
                        onChange={(e) => setForme(e.target.value || null)}
                        className={`h-full min-h-[48px] w-full appearance-none rounded-2xl border bg-white/60 dark:bg-white/[0.03] pl-4 pr-10 text-sm dark:text-white focus:outline-hidden focus:border-primary/60 transition-colors duration-200 ${
                            forme
                                ? "border-primary/50 text-[#B83C12] dark:text-[#FF8A5B] font-semibold"
                                : "border-gray-200/70 dark:border-white/10 text-gray-600 dark:text-lightBlue"
                        }`}
                    >
                        <option value="">{t.toutesFormes}</option>
                        {FORMES.map((f) =>
                            comptes.parForme[f] ? (
                                <option key={f} value={f}>
                                    {t.formes[f]} ({comptes.parForme[f]})
                                </option>
                            ) : null,
                        )}
                    </select>
                    <SolarIcon
                        name="alt-arrow-down-linear"
                        className="absolute right-3.5 top-1/2 -translate-y-1/2 text-base text-gray-400 dark:text-lightBlue/60 pointer-events-none"
                    />
                </div>
            </div>

            <div className="mt-3 -mx-4 px-4 sm:mx-0 sm:px-0 overflow-x-auto scrollbar-hide">
                <div role="group" aria-label={t.axeMoment} className="flex gap-2 w-max sm:w-auto sm:flex-wrap">
                    <Pastille actif={!moment} onClick={() => setMoment(null)} compte={outils.length}>
                        {t.toutes}
                    </Pastille>
                    {moments.map((m) => (
                        <Pastille
                            key={m}
                            actif={moment === m}
                            onClick={() => setMoment(moment === m ? null : m)}
                            compte={comptes.parMoment[m] || 0}
                        >
                            <SolarIcon name={ICONE_MOMENT[m]} className="text-base" />
                            {libellesMoments[m]}
                        </Pastille>
                    ))}
                </div>
            </div>

            {/* Le compteur ne parle que quand on a filtré : sans filtre, la
                pastille « Tout » porte déjà le total. */}
            {filtre && retenus.length > 0 && (
                <p role="status" className="mt-5 mb-0 text-sm text-gray-600 dark:text-lightBlue">
                    {retenus.length === 1
                        ? t.resultatUn
                        : t.resultats.replace("{n}", retenus.length)}
                </p>
            )}

            {retenus.length === 0 ? (
                /* L'encart occupe la hauteur d'une première section, et propose
                   de défaire les filtres plutôt que de laisser le lecteur
                   chercher lequel a tout vidé. */
                <div className="mt-8 mb-16 min-h-[15rem] flex items-center justify-center rounded-2xl border border-dashed border-gray-300/80 dark:border-white/15 px-6 py-12 text-center">
                    <div>
                        <SolarIcon name="magnifer-linear" className="text-3xl text-gray-300 dark:text-lightBlue/30" />
                        <p className="text-base font-semibold dark:text-white mt-3 mb-1.5">{t.rienTitre}</p>
                        <p className="text-sm text-gray-600 dark:text-lightBlue mb-0 max-w-sm mx-auto">
                            {t.rienAide.replace("{n}", outils.length)}
                        </p>
                        <button
                            type="button"
                            onClick={() => {
                                setMoment(null);
                                setForme(null);
                                setRecherche("");
                            }}
                            className="inline-flex items-center gap-2 mt-5 rounded-full border border-primary/50 bg-primary/10 px-4 py-2 min-h-[44px] text-sm font-semibold text-[#B83C12] dark:text-[#FF8A5B] transition-colors duration-200 hover:bg-primary/15"
                        >
                            <SolarIcon name="restart-linear" className="text-base" />
                            {t.toutAfficher}
                        </button>
                    </div>
                </div>
            ) : (
                <div className="mt-8 mb-16 space-y-8">
                    {sections.map(([cle, liste]) => (
                        <section key={cle}>
                            <h2 className="mb-2.5 flex items-center gap-2 text-[11px] font-semibold text-gray-600 dark:text-grayDark-400 uppercase tracking-wider">
                                {ICONE_MOMENT[cle] && <SolarIcon name={ICONE_MOMENT[cle]} className="text-sm" />}
                                {libellesMoments[cle] || cle}
                            </h2>
                            <ul className="m-0 list-none overflow-hidden rounded-2xl border border-gray-200/70 dark:border-white/10 bg-white/60 dark:bg-white/[0.03] p-0 divide-y divide-gray-200/60 dark:divide-white/[0.07]">
                                <AnimatePresence initial={false}>
                                    {liste.map((o) => (
                                        <motion.li
                                            key={o.cle}
                                            initial={{ opacity: 0 }}
                                            animate={{ opacity: 1 }}
                                            exit={{ opacity: 0 }}
                                            transition={{ duration: 0.18, ease: "easeOut" }}
                                        >
                                            <Link
                                                href={lien(o)}
                                                className="group flex items-start gap-3.5 px-4 py-3.5 sm:px-5 transition-colors duration-200 hover:bg-white dark:hover:bg-white/[0.04]"
                                            >
                                                <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary/[0.07] dark:bg-primary/[0.12]">
                                                    <SolarIcon
                                                        name={o.icone || ICONE_FORME[o.type] || "calculator-minimalistic-linear"}
                                                        className="w-5 h-5 text-primary"
                                                    />
                                                </span>
                                                <div className="min-w-0 flex-1">
                                                    <h3 className="mb-0 text-[15px] font-semibold leading-snug text-gray-900 dark:text-white transition-colors group-hover:text-[#B83C12] dark:group-hover:text-[#FF8A5B]">
                                                        {o.nom}
                                                    </h3>
                                                    {/* Coupée à l'écran, entière dans le HTML. */}
                                                    <span className="mt-0.5 block text-sm text-gray-600 dark:text-lightBlue line-clamp-2 sm:line-clamp-1">
                                                        {o.description}
                                                    </span>
                                                    {reponses[o.cle] && (
                                                        <ReponseDeLOutil reponse={reponses[o.cle]} t={t} locale={locale} />
                                                    )}
                                                </div>
                                                <span className="mt-2 hidden shrink-0 items-center gap-1 text-sm font-medium text-[#B83C12] dark:text-[#FF8A5B] sm:inline-flex">
                                                    {t.verbes[o.type]}
                                                    <SolarIcon name="arrow-right-linear" className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" />
                                                </span>
                                            </Link>
                                        </motion.li>
                                    ))}
                                </AnimatePresence>
                            </ul>
                        </section>
                    ))}
                </div>
            )}
        </div>
        </MotionConfig>
    );
}
